import type { Accessor, Component, JSX } from 'solid-js';
import type { EffectPackageControlValue, EffectPackageItem, EffectZoneNames } from './effectPackages';

/**
 * ONE node of a card whose settings are a tree (CONTRACT-console B84 / S34).
 *
 * Every tree-shaped card — Auto VJ's sets, units, steps and layers; Audio reactive's baseline and
 * reactive layers; any compound card after them — reaches the one tree view as this shape, and the view
 * has no code for any of them. An adapter per data model builds these (`screens/treeCards.ts` is the one
 * registry that knows which cards have one).
 *
 * A node is read through its fields, which an adapter implements as GETTERS over its own reactive
 * store, so the view re-reads a name or a value without rebuilding a row, a field or a slider.
 */
export interface SettingsTreeNode {
  id: string;
  /** What the tree row says. */
  readonly name: string;
  /** The zone word a layer carries beside its name ("Baseline" / "Reactive"). */
  readonly tag?: string;
  /** One line under a COLLAPSED node, built from values it already holds. */
  readonly summary?: string;
  /** The ids of the node's children, in order. */
  readonly children: readonly string[];
  /**
   * The node's own parameters in the SAME spec a flat card is drawn from (a manifest's parameters,
   * order and sync block), rendered by the one parameter row every flat card uses.
   */
  readonly parameters?: EffectPackageItem;
  readonly values?: Record<string, EffectPackageControlValue>;
  /** Whether the node's music sync is on, for a parameter the beat lock takes over. */
  readonly synced?: boolean;
  readonly zoneNames?: EffectZoneNames;
  /** The address prefix of the node's parameter rows (their test ids). */
  readonly parameterPrefix?: string;
  onParameters?: (values: Record<string, EffectPackageControlValue>, key: string, final: boolean) => void;
  committing?: (key: string) => boolean;
  /** The item's actions: icon actions sit beside its heading, the rest go in its ⋯ menu. */
  readonly actions: readonly SettingsTreeAction[];
  /** The item's own identity control, which heads the detail pane (a name field, a layer's effect picker). */
  head?: () => JSX.Element;
  /** Controls that are the item's but are not manifest parameters (a layer's sync and driver). */
  controls?: () => JSX.Element;
  /**
   * What the item adds beneath itself (`+ Unit`, `+ Step`, a layer picker), at the foot of its detail.
   * An add hands what it made to `control.added`, so the new item is selected at once (B89).
   */
  add?: (control: SettingsTreeControl) => JSX.Element;
  /** The same add, as the plus at the right end of the item's tree row (B88). */
  readonly plus?: SettingsTreePlus;
  /** Whether the item is read-only here (permission or an offline controller). */
  readonly disabled: boolean;
}

export interface SettingsTreeAction {
  label: string;
  /** An icon action shows beside the item's heading; one without an icon is a ⋯ menu entry. */
  icon?: Component<{ size?: number }>;
  danger?: boolean;
  readonly disabled?: boolean;
  run: (view: SettingsTreeControl) => void;
}

/**
 * The plus on an item's tree row: the item's own add, run from the tree (B88).
 *
 * A plain add (a unit, a step) runs on a press. An add that needs a choice (a layer's package) offers
 * its choices in the add's own menu. Either way `run` makes the item exactly as the item's `add` does
 * and returns the new item's tree id, and the view selects it (B89).
 */
export interface SettingsTreePlus {
  /** The add's existing accessible name (`Add unit`, `Add step: Candle`, `Add`). */
  readonly label: string;
  readonly disabled?: boolean;
  /** What an add that needs a choice offers, in menu order; a plain add has none. */
  readonly choices?: readonly SettingsTreeChoice[];
  run: (choice?: string) => string | undefined;
}

export interface SettingsTreeChoice {
  value: string;
  name: string;
  /** The menu group the choice sits under; a choice with none sits above every group. */
  group?: string;
}

/** What an action may ask of the view it runs in. */
export interface SettingsTreeControl {
  select: (id: string) => void;
  /**
   * Show what an add just made: selected, every parent open, its row on screen and it in the right
   * pane. An id the tree does not hold (the add did not happen) changes nothing.
   */
  added: (id: string | undefined) => void;
  /** Put the cursor in the selected item's name field. */
  focusHead: () => void;
}

/**
 * The adapter's side of the interface: the roots, a node by id, and the one header slot.
 *
 * `node` returns the SAME object for the same id for as long as the node exists, and `undefined` once
 * it has gone.
 */
export interface SettingsTreeSource {
  roots: Accessor<readonly string[]>;
  node: (id: string) => SettingsTreeNode | undefined;
  /** The sheet header's fixed save slot for the selected item. */
  saveSlot?: (selectedId: string) => JSX.Element;
  /** The save slot's staff detail, which cannot fit a fixed header slot and so heads the item instead. */
  saveDetail?: (selectedId: string) => JSX.Element;
  /** Told when the viewer selects an item (Auto VJ loads the set holding it). */
  onSelect?: (id: string) => void;
  /**
   * The tree's own add at its first level, for a tree whose first level no row adds to (Audio reactive's
   * layers). It is the card's own add, and the view draws it as the plus of one last row (B88).
   */
  readonly plus?: SettingsTreePlus;
}

/** One visible row of the tree, in document order. */
export interface SettingsTreeRow {
  id: string;
  depth: number;
  parent: string;
  hasChildren: boolean;
  open: boolean;
}

type NodeReader = (id: string) => Pick<SettingsTreeNode, 'name' | 'children'> | undefined;

function matches(name: string, needle: string): boolean {
  return name.toLocaleLowerCase().includes(needle);
}

/**
 * Every row the tree shows, in order, with each row's depth.
 *
 * Without a search, a node's children show while it is open. With one, a node shows when its own name
 * matches or one of its descendants' does, and every node with a matching descendant is shown open, so
 * a match is never shown without the parents that say where it is. Searching opens nothing: the open
 * set is untouched, and clearing the search puts the tree back as it was.
 */
export function visibleTreeRows(
  roots: readonly string[],
  node: NodeReader,
  open: ReadonlySet<string>,
  query = '',
): SettingsTreeRow[] {
  const needle = query.trim().toLocaleLowerCase();
  const rows: SettingsTreeRow[] = [];
  const subtreeMatches = new Map<string, boolean>();
  function subtreeMatch(id: string): boolean {
    const known = subtreeMatches.get(id);
    if (known !== undefined) return known;
    const current = node(id);
    const result = Boolean(current && (matches(current.name, needle)
      || current.children.some((child) => subtreeMatch(child))));
    subtreeMatches.set(id, result);
    return result;
  }
  function walk(ids: readonly string[], depth: number, parent: string): void {
    for (const id of ids) {
      const current = node(id);
      if (!current) continue;
      if (needle && !subtreeMatch(id)) continue;
      const descendantMatch = Boolean(needle) && current.children.some((child) => subtreeMatch(child));
      const shownOpen = needle ? descendantMatch : open.has(id);
      rows.push({ id, depth, parent, hasChildren: current.children.length > 0, open: shownOpen && current.children.length > 0 });
      if (shownOpen) walk(current.children, depth + 1, id);
    }
  }
  walk(roots, 1, '');
  return rows;
}

/** The parent of every node in the tree, whether or not it is showing. */
export function treeParents(roots: readonly string[], node: NodeReader): Map<string, string> {
  const parents = new Map<string, string>();
  const walk = (ids: readonly string[], parent: string): void => {
    for (const id of ids) {
      parents.set(id, parent);
      const current = node(id);
      if (current) walk(current.children, id);
    }
  };
  walk(roots, '');
  return parents;
}

export type TreeKeyResult =
  | { kind: 'focus'; id: string }
  | { kind: 'open'; id: string }
  | { kind: 'close'; id: string }
  | { kind: 'select'; id: string }
  | { kind: 'none' };

/**
 * The standard tree keyboard pattern over the rows on screen: ↑/↓ move between rows, → opens a closed
 * node or moves into an open one, ← closes an open node or moves to its parent, Enter selects.
 */
export function treeKey(rows: readonly SettingsTreeRow[], focused: string, key: string): TreeKeyResult {
  const index = rows.findIndex((row) => row.id === focused);
  const row = index >= 0 ? rows[index] : undefined;
  if (!row) return rows.length && (key === 'ArrowDown' || key === 'ArrowUp') ? { kind: 'focus', id: rows[0].id } : { kind: 'none' };
  switch (key) {
    case 'ArrowDown': return index + 1 < rows.length ? { kind: 'focus', id: rows[index + 1].id } : { kind: 'none' };
    case 'ArrowUp': return index > 0 ? { kind: 'focus', id: rows[index - 1].id } : { kind: 'none' };
    case 'ArrowRight':
      if (!row.hasChildren) return { kind: 'none' };
      if (!row.open) return { kind: 'open', id: row.id };
      return rows[index + 1]?.parent === row.id ? { kind: 'focus', id: rows[index + 1].id } : { kind: 'none' };
    case 'ArrowLeft':
      if (row.open) return { kind: 'close', id: row.id };
      return row.parent ? { kind: 'focus', id: row.parent } : { kind: 'none' };
    case 'Enter': return { kind: 'select', id: row.id };
    default: return { kind: 'none' };
  }
}
